import React, { useState, useEffect } from 'react';
import { getCancelReasonsForDropdown, CancelReason } from '../services/api';

const OTHERS_VALUE = '__others__';

interface CancelBookingModalProps {
  isOpen: boolean;
  onConfirm: (cancellationReason: string) => Promise<void>;
  onCancel: () => void;
}

const CancelBookingModal: React.FC<CancelBookingModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  const [reasons, setReasons] = useState<CancelReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedReasonId('');
      setCustomReason('');
      setError('');
      setLoadingReasons(true);
      getCancelReasonsForDropdown()
        .then((res) => setReasons(res.data.data || []))
        .catch(() => setReasons([]))
        .finally(() => setLoadingReasons(false));
    }
  }, [isOpen]);

  const getReasonText = (): string => {
    if (selectedReasonId === OTHERS_VALUE) {
      return customReason.trim();
    }
    const r = reasons.find((x) => x._id === selectedReasonId);
    return r ? (r.spec?.reason ?? (r as any).metadata?.reason ?? '') : '';
  };

  const handleConfirm = async () => {
    const reasonText = getReasonText();
    if (!reasonText) {
      setError(selectedReasonId === OTHERS_VALUE ? 'Please enter a reason.' : 'Please select a reason for cancellation.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await onConfirm(reasonText);
      onCancel();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="drawer-overlay" onClick={onCancel}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h3 className="drawer-title">Cancel Booking</h3>
          <button type="button" className="drawer-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        <div className="drawer-body">
          <p style={{ margin: '0 0 16px', opacity: 0.9 }}>
            Select a reason for cancelling this booking, or choose &quot;Others&quot; to enter your own.
          </p>
          {loadingReasons ? (
            <p>Loading reasons...</p>
          ) : (
            <>
              <div className="form-group">
                <label>Reason for cancellation</label>
                <select
                  value={selectedReasonId}
                  onChange={(e) => {
                    setSelectedReasonId(e.target.value);
                    setError('');
                  }}
                  className={error && !selectedReasonId ? 'input-error' : ''}
                >
                  <option value="">-- Select reason --</option>
                  {reasons.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.spec?.reason ?? (r as any).metadata?.reason ?? ''}
                    </option>
                  ))}
                  <option value={OTHERS_VALUE}>Others</option>
                </select>
              </div>
              {selectedReasonId === OTHERS_VALUE && (
                <div className="form-group">
                  <label>Enter your reason</label>
                  <textarea
                    value={customReason}
                    onChange={(e) => {
                      setCustomReason(e.target.value);
                      setError('');
                    }}
                    placeholder="e.g. Patient declined transport"
                    rows={3}
                    style={{ resize: 'vertical', minHeight: '80px' }}
                  />
                </div>
              )}
            </>
          )}
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="drawer-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-submit"
            onClick={handleConfirm}
            disabled={submitting || loadingReasons}
            style={{ background: '#ff6b35', borderColor: '#ff6b35' }}
          >
            {submitting ? (
              <>
                <span className="loading-spinner btn-spinner" aria-hidden />
                Cancelling...
              </>
            ) : (
              'Yes, cancel booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelBookingModal;
